/**
 * Hook returning the per-tool gizmo callbacks shared by all four surface
 * gizmos (Footprint = Box / Cylinder, Poly, Inset).
 *
 * Each gizmo used to inline a near-identical set of `setHeight`,
 * `setProfileTarget`, `confirm`, `setDragging`, `setProfileFace`,
 * `setProfileEdge` factories that read the appropriate `surfaceTool`
 * draft slot and write back via `setSurfaceTool`. With the polymorphic
 * slot in place, those collapse to a single hook parameterised by `kind`.
 *
 * The Inset version exposes an extra `setInset` callback (the inset ring
 * gizmo's continuous distance update) that the other three don't need.
 */

import { useCallback } from "react";
import { useStore } from "../../app/store.ts";
import type {
  SurfaceFootprintDraftState,
  SurfaceInsetDraftState,
  SurfacePolyDraftState,
  SurfaceToolKind,
} from "../../app/storeTypes.ts";
import { selectSurfaceToolOfKind } from "../../app/surfaceToolSelectors.ts";
import type { SurfaceProfileTarget } from "../../modeling/surfaceProfiles.ts";

type DraftFor<K extends SurfaceToolKind> = K extends "box" | "cylinder"
  ? SurfaceFootprintDraftState
  : K extends "poly"
    ? SurfacePolyDraftState
    : K extends "inset"
      ? SurfaceInsetDraftState
      : never;

interface SurfaceToolGizmoCallbacks<K extends SurfaceToolKind> {
  /** Patch the active draft (no-op when the tool is not armed for `kind`). */
  patchDraft: (patch: Partial<DraftFor<K>>) => void;
  /** Update the orbit-suppression flag. */
  setDragging: (dragging: boolean) => void;
  /** Update the live extrude height. */
  setHeight: (h: number) => void;
  /** Update the profile target (face fill or edge ramp). */
  setProfileTarget: (target: SurfaceProfileTarget) => void;
  /** Convenience wrappers around `setProfileTarget`. */
  setProfileFace: () => void;
  setProfileEdge: (edgeIndex: number) => void;
  /** Run the kind-specific confirm action. */
  confirm: () => void;
}

function confirmFor(kind: SurfaceToolKind): () => void {
  switch (kind) {
    case "box":
      return () => useStore.getState().confirmSurfaceBox();
    case "cylinder":
      return () => useStore.getState().confirmSurfaceCylinder();
    case "poly":
      return () => useStore.getState().confirmSurfacePoly();
    case "inset":
      return () => useStore.getState().confirmSurfaceInset();
  }
}

export function useSurfaceToolGizmoCallbacks<K extends SurfaceToolKind>(
  kind: K,
): SurfaceToolGizmoCallbacks<K> {
  const patchDraft = useCallback(
    (patch: Partial<DraftFor<K>>) => {
      const cur = selectSurfaceToolOfKind(useStore.getState(), kind);
      if (!cur?.draft) return;
      useStore
        .getState()
        .setSurfaceTool({ ...cur, draft: { ...cur.draft, ...patch } });
    },
    [kind],
  );

  const setDragging = useCallback(
    (dragging: boolean) => {
      const cur = selectSurfaceToolOfKind(useStore.getState(), kind);
      if (!cur) return;
      useStore.getState().setSurfaceTool({ ...cur, active: dragging });
    },
    [kind],
  );

  const setHeight = useCallback(
    (h: number) => {
      // The cast through `as` is safe: every surface draft type has a
      // numeric `height` field. The discriminated union doesn't track
      // shared properties through `Partial<DraftFor<K>>` automatically.
      patchDraft({ height: h } as Partial<DraftFor<K>>);
    },
    [patchDraft],
  );

  const setProfileTarget = useCallback(
    (target: SurfaceProfileTarget) => {
      patchDraft({ profileTarget: target } as Partial<DraftFor<K>>);
    },
    [patchDraft],
  );

  const setProfileFace = useCallback(() => {
    setProfileTarget({ kind: "face" });
  }, [setProfileTarget]);

  const setProfileEdge = useCallback(
    (edgeIndex: number) => {
      setProfileTarget({ kind: "edge", edgeIndex });
    },
    [setProfileTarget],
  );

  // confirmFor is pure given `kind`; allocate once per render via useCallback
  // so the returned object's identity stays stable across re-renders.
  const confirm = useCallback(confirmFor(kind), [kind]);

  return {
    patchDraft,
    setDragging,
    setHeight,
    setProfileTarget,
    setProfileFace,
    setProfileEdge,
    confirm,
  };
}
